/* eslint-disable no-console */
/* eslint-disable import/no-cycle */
import jwtDecode from "jwt-decode";

import { getRoleNameProfileSA } from "../api/MasterUser";
import { HttpService } from "../constants/HTTPService";
import { IJwtTokenData } from "../interfaces/IJwtTokenData";
import { store } from "../store/store";
import { fetchAndDisplayProfileImage, GetGraphData } from "./msGraph";
import { getMicrosoftTokenSilent } from "./new-azure-ad";
import { getAuthToken, removeAuthToken, setAuthToken } from "./storage";
import { setUserAvatar, setUserProfile, setUserRole } from "../store/auth/slice";

const TOKEN_REFRESH_COOLDOWN = 2000;
const LOCK_CLEAR_DELAY = 500;

let tokenRefreshPromise: Promise<string | null> | null = null;
let lastRefreshTimestamp = 0;

export async function refreshToken(): Promise<string | null> {
  const now = Date.now();
  if (now - lastRefreshTimestamp < TOKEN_REFRESH_COOLDOWN) {
    const currentToken = getAuthToken();
    if (currentToken?.value && !currentToken.isExpired) {
      console.log("[Token] Using recently refreshed token");
      return currentToken.value;
    }
  }

  if (tokenRefreshPromise) {
    console.log("[Token] Refresh in progress, reusing existing promise");
    return tokenRefreshPromise;
  }

  console.log("[Token] Starting new token refresh");
  tokenRefreshPromise = (async () => {
    try {
      const msalAccessToken = await getMicrosoftTokenSilent();
      if (!msalAccessToken) {
        console.log("[Token] Failed to get MSAL token");
        removeAuthToken();
        return null;
      }

      const userData = await GetGraphData(msalAccessToken);
      if (!userData.mail.includes("@binus.edu")) {
        console.log("[Token] User email not from binus.edu domain");
        removeAuthToken();
        return null;
      }

      const authResponse = await HttpService.getAuthorizationTokenMsal(
        msalAccessToken
      );
      const { tokenData } = authResponse.data;
      const decodedToken: IJwtTokenData = jwtDecode(tokenData.token);

      setAuthToken(tokenData.token);
      lastRefreshTimestamp = Date.now();

      const [profileImageBase64, roleProfile] = await Promise.all([
        fetchAndDisplayProfileImage(msalAccessToken),
        getRoleNameProfileSA(decodedToken.BinusianId),
      ]);

      await Promise.all([
        store.dispatch(setUserProfile(decodedToken)),
        store.dispatch(setUserRole(roleProfile)),
        store.dispatch(setUserAvatar(profileImageBase64)),
      ]);

      return tokenData.token;
    } catch (error) {
      console.error("[Token] Failed to refresh token:", error);
      removeAuthToken();
      return null;
    } finally {
      setTimeout(() => {
        tokenRefreshPromise = null;
      }, LOCK_CLEAR_DELAY);
    }
  })();

  return tokenRefreshPromise;
}
